import React, { Component } from "react";
import { Button } from "react-bootstrap";
import { LuCopy, LuCheck, LuChevronDown, LuChevronUp } from "react-icons/lu";
import { CgDetailsMore } from "react-icons/cg";

import Tooltip from "../../../controls/common/tooltip";
import Clipboard from "../../../controls/common/clipboard";
import ThreadItemAction from "./threadItemAction";
import { MarkdownBody } from "./markdown";
import { ThreadImage, FileChip, UnsupportedBlock, isImageBlock } from "./fileBlocks";
import Utils from "context/utils";

/**
 * ThreadItems — a list of thread items, rendered the way the thread widget renders them.
 *
 * Lifted out of that widget because a second surface needed the SAME rendering and had grown its
 * own: Mission Control shows a cell run's raw transcript, which is a thread of exactly these items,
 * and two renderings of one thing is how they drift — one gains a details panel, the other a copy
 * button, and a person moving between them learns two products.
 *
 * What it owns is the per-item VIEW state, because that is what a list of these is: which bodies are
 * expanded, which panel is open under which item, and which bodies overflow their cap and so need a
 * toggle at all (measured after layout, since only the browser knows).
 *
 * What it does NOT own is where the items came from, who wrote them, or whether anything may be
 * posted back. Loading, paging and the composer stay with the caller — the thread widget has all
 * three, Mission Control has the first two and deliberately no composer, and neither has to explain
 * itself to the other.
 *
 * Props:
 *   items              — the items, chronological
 *   contentView        — "markdown" | "raw" | "json"
 *   currentUserId      — whose messages sit on the right; null for none
 *   timezone           — for the absolute timestamp tooltip
 *   resolveAuthorName  — (userId) => string
 *   resolveProfilePath — (userId) => string | null
 *   onRetry            — (item) => void, for a failed optimistic post; omit where there are none
 *   fileContext        — { api, revisionId, sessionId } handed to `file` content blocks so they can
 *                         fetch their bytes; omit and they still render, but never fetch
 *
 * And three optional seams for a caller that knows what its items MEAN, all rendered inside the
 * same bubble with the same actions, author and panels:
 *   renderHead         — (item) => node, a caption above the body and outside the collapse
 *   renderContent      — (item, contentView, isExpanded) => node, the body itself
 *   renderFooter       — (item) => node, below the body — costs, errors, what a call produced
 */
/**
 * What the copy action puts on the clipboard: the WHOLE item, as JSON.
 *
 * The visible line is often just a label over the thing worth having. On a cell's threads the
 * metadata IS the content — a `tool_call`'s input, a `turn_end`'s stats, an artifact's alias and
 * size all live there — and even on an ordinary message the ids and timestamps are what somebody
 * pasting into a bug report needs. Copying the prose hands over the label and drops the record.
 *
 * Falls back to the prose only if the item cannot be serialised at all, which is better than
 * copying nothing.
 */
export function copyText(item) {
  try {
    return JSON.stringify(item, null, 2);
  } catch (e) {
    return (Array.isArray(item.content) ? item.content : [])
      .filter(part => part && part.type === "text" && typeof part.text === "string")
      .map(part => part.text)
      .join("\n\n");
  }
}


export default class ThreadItems extends Component {
  constructor(props) {
    super(props);
    this.state = {
      expandedContent: new Set(),
      expandedPanel: new Map(),
      overflowingItems: new Set(),
      // Per-block overflow, keyed `${itemId}:${index}` — a superset-by-item of overflowingItems.
      // Drives the fade: a block is faded only when it is actually clipped, never just because it
      // is collapsed. (overflowingItems stays item-level because ONE expand toggle acts on the item.)
      overflowingBlocks: new Set()
    };
    this.contentRefs = {};
  }

  componentDidMount() {
    // Measure on first paint too, not only on update: a genuinely clipped block should surface its
    // fade and expand toggle together, immediately, rather than waiting for the next render.
    this.checkContentOverflow();
  }

  componentDidUpdate() {
    this.checkContentOverflow();
  }

  toggleInSet(stateKey, itemId) {
    if ( !itemId ) return;
    const next = new Set(this.state[stateKey]);
    if ( next.has(itemId) ) {
      next.delete(itemId);
    } else {
      next.add(itemId);
    }
    this.setState({ [stateKey]: next });
  }

  toggleContent(itemId) {
    this.toggleInSet("expandedContent", itemId);
  }

  // Open `panel` under the item, or close it if it is already the open one.
  // Switching panels replaces the entry, so metadata and details can never be
  // on screen together.
  togglePanel(itemId, panel) {
    if ( !itemId ) return;
    const next = new Map(this.state.expandedPanel);
    if ( next.get(itemId) === panel ) {
      next.delete(itemId);
    } else {
      next.set(itemId, panel);
    }
    this.setState({ expandedPanel: next });
  }

  openPanel(itemId) {
    return this.state.expandedPanel.get(itemId) || null;
  }

  registerContentRef = (itemId, index, el) => {
    if ( !itemId ) return;
    if ( !this.contentRefs[itemId] ) this.contentRefs[itemId] = [];
    if ( el ) {
      this.contentRefs[itemId][index] = el;
    } else if ( this.contentRefs[itemId] ) {
      this.contentRefs[itemId][index] = null;
    }
  }

  // Compare rendered scroll-height vs offset-height for each tracked
  // content block and flip overflowingItems accordingly. Items in the
  // expanded state are skipped (no max-height applied, so they can't
  // be in overflow). Called from componentDidUpdate so the toggle
  // shows/hides as soon as layout settles.
  checkContentOverflow() {
    const nextItems = new Set();
    const nextBlocks = new Set();
    const expanded = this.state.expandedContent;
    for ( const itemId of Object.keys(this.contentRefs) ) {
      if ( expanded.has(itemId) ) {
        // Carry forward — once flagged, an item stays "could overflow" until we get a fresh
        // measurement in its collapsed state. We can't measure while expanded because the cap is
        // lifted. Carry the per-block flags forward the same way (they only matter once re-collapsed).
        if ( this.state.overflowingItems.has(itemId) ) nextItems.add(itemId);
        this.state.overflowingBlocks.forEach((key) => {
          if ( key.startsWith(itemId + ":") ) nextBlocks.add(key);
        });
        continue;
      }
      const refs = this.contentRefs[itemId] || [];
      for ( let i = 0; i < refs.length; i++ ) {
        const el = refs[i];
        if ( el && el.scrollHeight > el.offsetHeight + 1 ) {
          nextItems.add(itemId);
          nextBlocks.add(itemId + ":" + i);
        }
      }
    }
    if ( this.setsEqual(nextItems, this.state.overflowingItems)
      && this.setsEqual(nextBlocks, this.state.overflowingBlocks) ) {
      return;
    }
    this.setState({ overflowingItems: nextItems, overflowingBlocks: nextBlocks });
  }

  setsEqual(a, b) {
    if ( a.size !== b.size ) return false;
    for ( const v of a ) {
      if ( !b.has(v) ) return false;
    }
    return true;
  }

  renderTextContent(text, contentView) {
    if ( typeof text !== "string" ) {
      return null;
    }
    if ( "raw" === contentView ) {
      return <pre className="playground-thread-text-raw">{text}</pre>;
    }
    if ( "json" === contentView ) {
      let pretty = text;
      try { pretty = JSON.stringify(JSON.parse(text), null, 2); } catch (e) { /* fall through */ }
      return <pre className="playground-thread-text-json">{pretty}</pre>;
    }
    // Markdown (default)
    return <MarkdownBody text={text} />;
  }

  renderItemActions(item) {
    // Don't surface actions for in-flight optimistic posts — there's no
    // server item to act on yet, and showing them would be misleading.
    if ( item.__pending ) return null;

    // Measured overflow, OR a caller that already knows. Measurement is the better test in a
    // browser — it offers the toggle only when the body really is clipped — but it needs layout,
    // and a caller whose bodies are known to be long (a worker's transcript carries whole files)
    // can say so without waiting to be measured.
    const overflows = this.state.overflowingItems.has(item.id)
      || !!(this.props.isLong && this.props.isLong(item));
    const contentExpanded = this.state.expandedContent.has(item.id);
    const openPanel = this.openPanel(item.id);

    return (
      <span className="playground-thread-actions">
        <ThreadItemAction
          tooltip="Copy to clipboard"
          confirmTooltip="Copied"
          icon={<LuCopy />}
          confirmIcon={<LuCheck />}
          onActivate={() => Clipboard.to(copyText(item))}
        />
        {overflows &&
          <ThreadItemAction
            tooltip={contentExpanded ? "Collapse" : "Expand"}
            icon={contentExpanded ? <LuChevronUp /> : <LuChevronDown />}
            onActivate={() => this.toggleContent(item.id)}
          />
        }
        {/* ONE panel, showing the WHOLE item. It used to be two: `metadata`, which showed a
            fragment of the item and only when there was one, and `details`, a hand-picked table of
            three ids. Between them a reader could see most of an item and never all of it — and had
            to know which of two buttons held the field they were after. The item as it is on the
            wire answers both, and cannot go stale as fields are added to it. */}
        <ThreadItemAction
          tooltip={"json" === openPanel ? "Hide item JSON" : "Show item JSON"}
          icon={<CgDetailsMore />}
          onActivate={() => this.togglePanel(item.id, "json")}
        />
      </span>
    );
  }

  renderItemContent(item, contentView, isExpanded) {
    // A caller that knows what these items MEAN may render the body itself. Mission Control does:
    // a worker's transcript is tool calls, results and turn boundaries, and it shows the command a
    // `tool_call` actually ran rather than the truncated preview the worker put in `content`. It is
    // still wrapped here, with the same ref, so the collapse cap and the expand toggle it surfaces
    // work identically to a default body — the seam is what a body SAYS, not how it behaves.
    const custom = this.props.renderContent
      ? this.props.renderContent(item, contentView, isExpanded) : null;
    if ( custom !== null && custom !== undefined && custom !== false ) {
      const overflowing = !isExpanded && this.state.overflowingBlocks.has(item.id + ":0");
      const cls = "playground-thread-content-item"
        + (isExpanded ? "" : " is-collapsed")
        + (overflowing ? " is-overflowing" : "");
      return (
        <div className={cls} ref={(el) => this.registerContentRef(item.id, 0, el)}>{custom}</div>
      );
    }
    return (item.content || []).map((contentItem, ci) => {
      let inner = null;
      if ( "text" === contentItem.type ) {
        inner = this.renderTextContent(contentItem.text, contentView);
      } else if ( "json" === contentItem.type ) {
        const text = JSON.stringify(contentItem.json, null, 2);
        inner = <pre className="playground-thread-text-json">{text}</pre>;
      } else if ( "file" === contentItem.type && contentItem.file ) {
        inner = isImageBlock(contentItem)
          ? <ThreadImage fileContext={this.props.fileContext} block={contentItem} />
          : <FileChip fileContext={this.props.fileContext} block={contentItem} />;
      }
      if ( inner === null ) {
        inner = <UnsupportedBlock type={contentItem.type} />;
      }
      // Wrap each content item so a sibling-selector rule can draw the
      // dashed separator between them and so collapsed-state max-height
      // can be applied. The callback ref hooks the element into
      // checkContentOverflow() for measuring rendered vs intrinsic
      // height — that determines whether the expand toggle surfaces, and
      // whether THIS block gets the fade (is-overflowing) or not.
      const overflowing = !isExpanded && this.state.overflowingBlocks.has(item.id + ":" + ci);
      const cls = "playground-thread-content-item"
        + (isExpanded ? "" : " is-collapsed")
        + (overflowing ? " is-overflowing" : "");
      return (
        <div
          key={ci}
          className={cls}
          ref={(el) => this.registerContentRef(item.id, ci, el)}
        >
          {inner}
        </div>
      );
    });
  }

  renderItem(item, index, contentView) {
    const isSelf = item.user_id && this.props.currentUserId && item.user_id === this.props.currentUserId;
    const itemKey = item.id || item.__tempId || index;
    const timezone = this.props.timezone;
    const timeAgo = item.created_at ? Utils.formatTimeAgo(item.created_at) : null;
    const timeAbs = item.created_at ? Utils.formatDateTime(item.created_at, timezone) : null;
    const contentExpanded = this.state.expandedContent.has(item.id);
    const openPanel = this.openPanel(item.id);
    const body = this.renderItemContent(item, contentView, contentExpanded);

    const failedFooter = item.__failed && (
      <div className="playground-thread-failed">
        <span>Failed to send.</span>
        <Button
          size="sm"
          className="playground-thread-failed-retry"
          onClick={() => this.props.onRetry(item)}
        >
          Retry
        </Button>
      </div>
    );

    const sideClass = isSelf ? "playground-thread-row-self" : "playground-thread-row-other";
    let bubbleClass = isSelf ? "playground-thread-bubble-self" : "playground-thread-bubble-other";
    if ( item.__pending ) bubbleClass += " playground-thread-bubble-pending";
    if ( item.__failed ) bubbleClass += " playground-thread-bubble-failed";

    // Self messages don't get an author label — it's redundant ("self"
    // says nothing useful next to a bubble on the right). Other-side
    // bubbles show a clickable author name.
    let authorEl = null;
    if ( !isSelf ) {
      const authorName = this.props.resolveAuthorName(item.user_id);
      const profilePath = this.props.resolveProfilePath(item.user_id);
      authorEl = profilePath
        ? <a
            className="playground-thread-meta-author playground-thread-meta-author-link"
            href={profilePath}
            target="_blank"
            rel="noreferrer"
          >{authorName}</a>
        : <span className="playground-thread-meta-author">{authorName}</span>;
    }

    return (
      <div key={itemKey} className={"playground-thread-row " + sideClass}>
        <div className={"playground-thread-bubble " + bubbleClass}>
          {/* Above the body and outside the collapse, because it says what the item IS — a caption
              a reader needs whether or not the body is expanded. */}
          {this.props.renderHead ? this.props.renderHead(item) : null}
          {body}
          {this.props.renderFooter ? this.props.renderFooter(item) : null}
          {"json" === openPanel &&
            <div className="playground-thread-metadata-block">
              <div className="playground-thread-metadata-label">item</div>
              <pre className="playground-thread-metadata-json">
                {JSON.stringify(item, null, 2)}
              </pre>
            </div>
          }
          {failedFooter}
        </div>
        <div className="playground-thread-meta">
          {authorEl}
          {timeAgo &&
            <>
              {authorEl && <span className="playground-thread-meta-sep">&middot;</span>}
              <Tooltip text={timeAbs}>
                <span className="playground-thread-meta-time">{timeAgo}</span>
              </Tooltip>
            </>
          }
          {(() => {
            const actionsEl = this.renderItemActions(item);
            if ( !actionsEl ) return null;
            return (
              <>
                <span className="playground-thread-meta-sep">&middot;</span>
                {actionsEl}
              </>
            );
          })()}
        </div>
      </div>
    );
  }

  render() {
    const { items, contentView } = this.props;
    return (items || []).map((item, index) => this.renderItem(item, index, contentView));
  }
}
